import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
// configs
import {
    EX7982,
    EX8046,
    INIT_FORM_FREE_TRIAL_OPH,
    UTM_CAMPAIGN_SUCCESS,
    UTM_CONTENT_SUCCESS,
    UTM_MEDIUM_SUCCESS,
    UTM_SOURCE_SUCCESS,
    UTM_TERM_SUCCESS,
} from 'config/flags';
// apis
import { createAccountByFreeTrial } from 'api';
// components
import { Form as FormRsuite, Input, InputGroup, SelectPicker, InputNumber } from 'rsuite';
import { ExAutocompleteGmaps, ExButton } from '@commons/EXComponentsLibrary';
import { ErrorModal } from '@commons/components/ErrorModal';
import { DropdownDrugManufacturers } from './DropdownDrugManufacturers';
import { Disclaimer } from '@commons/modules/Account/Disclaimer';
// interfaces
import { IForm, IFormErrors, IFormTouched, IShowModal } from './interfaces';
// utils && hooks
import { checkMissingKeys, checkQuantityDrugManufacturers, formattedPhone, includesOthers } from './utils';
import { LABELS, MESSAGE, TEXT } from './enum';
// styles
import { useMediaQuery } from '@mui/material';
import styles from './Form.module.css';
import animationStyles from '../../../animations.module.css';

const _COUNTRYS = [
    /* {
        name: 'Argentina',
        abbr: 'AR',
        code: '54',
    }, */
    {
        name: 'Colombia',
        abbr: 'CO',
        code: '57',
    },
    {
        name: 'Mexico',
        abbr: 'MX',
        code: '52',
    },
    // En el caso de necesitar agregar paises
    // { name: 'España', abbr: 'ES', code: '34' },
    // { name: 'Estados Unidos', abbr: 'US', code: '1' },
    // { name: 'Brazil', abbr: 'BR', code: '55' },
];

const VOLUME_BY_COUNTRY = {
    /* AR: [
        { label: 'Hasta AR$ 2.000.000', value: 'A' },
        { label: 'Entre AR$ 2.000.000 y AR$ 20.000.000', value: 'B' },
        { label: 'Más de AR$ 20.000.000', value: 'C' },
        { label: 'Prefiero no decirlo', value: null },
    ], */
    CO: [
        { label: 'Hasta COL$ 8.000.000', value: 'A' },
        { label: 'Entre COL$ 8.000.000 y COP$ 80.000.000', value: 'B' },
        { label: 'Más de COL$ 80.000.000', value: 'C' },
        { label: 'Prefiero no decirlo', value: 'D' },
    ],
    MX: [
        { label: 'Hasta MX$ 40.000', value: 'A' },
        { label: 'Entre MX$ 40.000 y MXN$ 400.000', value: 'B' },
        { label: 'Más de MX$ 400.000', value: 'C' },
        { label: 'Prefiero no decirlo', value: 'D' },
    ],
};

const data = _COUNTRYS.map((item) => ({
    label: item.abbr,
    value: `${item.code}`,
}));

const KEYS = [
    'first_name',
    'last_name',
    'email',
    'country_alfa_2',
    'phone_number',
    'pharmacy_name',
    'pharmacy_address',
    'monthly_purchase_volume',
    'pos_count',
    'drug_manufacturer_count',
    ...(EX8046 ? ['purchasing_distributors'] : []),
];

const Form = ({ setStep, formValues, setFormValues }: IForm) => {
    const router = useRouter();
    const [formTouched, setFormTouched] = useState<IFormTouched>({});
    const [formErrors, setFormErrors] = useState<IFormErrors>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [animationStep, setAnimationStep] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<IShowModal>({
        state: false,
        message: '',
    });
    const selectCountryRef: any = useRef(null);
    const parentRef: any = useRef(null);
    const isMobile = useMediaQuery('(max-width:768px)');

    useEffect(() => {
        setFormTouched({});
        setFormErrors({});
        setIsLoading(false);
    }, []);

    const handleBlur = (field, value) => {
        setFormTouched((prev) => ({ ...prev, [field]: value }));
    };

    const handleChangePhone = (value) => {
        const onlyNumbers = value.replace(/\D/g, ''); // Elimina todo excepto números
        if (onlyNumbers.length === 0) {
            setFormErrors((prev) => ({
                ...prev,
                phone_number: TEXT.FIELD_REQUIRED,
            }));
            // Actualiza el valor en formValues
            setFormValues((prev) => ({
                ...prev,
                phone_number: '',
            }));
            return;
        }

        setFormErrors((prev) => ({
            ...prev,
            phone_number: false,
        }));

        setFormValues((prev) => ({
            ...prev,
            phone_number: formattedPhone(onlyNumbers),
        }));
    };

    const showErrorModal = (message) => {
        setShowModal({ state: true, message });
    };

    const handleSubmit = async () => {
        const encodedToken = formValues.email ? btoa(encodeURIComponent(formValues.email)) : '';
        if (!isLoading) {
            const addPurchasingDistributors =
                formValues?.drug_manufacturer_count?.length > 0
                    ? includesOthers(formValues?.drug_manufacturer_count)
                    : false;

            const REQUIRED_KEYS = EX8046
                ? KEYS.filter(
                      (key) =>
                          key !== 'monthly_purchase_volume' &&
                          (addPurchasingDistributors || key !== 'purchasing_distributors'),
                  )
                : KEYS.filter((key) => key !== 'monthly_purchase_volume');

            if (
                checkMissingKeys(formValues, REQUIRED_KEYS) ||
                Object.keys(formValues)?.length === 0 ||
                Object.keys(formErrors)?.length === 0 ||
                !Object.values(formErrors).every((value) => !value)
            ) {
                // Levantar errores
                setFormErrors((prevState) => {
                    const updatedErrors = { ...prevState };
                    REQUIRED_KEYS.forEach((key) => {
                        if (!Object.prototype.hasOwnProperty.call(updatedErrors, key)) {
                            updatedErrors[key] = TEXT.FIELD_REQUIRED;
                        }
                    });
                    return updatedErrors;
                });

                setFormTouched((prevState) => {
                    const updatedErrors = { ...prevState };
                    REQUIRED_KEYS.forEach((key) => {
                        if (!Object.prototype.hasOwnProperty.call(updatedErrors, key)) {
                            updatedErrors[key] = true;
                        }
                    });
                    return updatedErrors;
                });
                return;
            }
            setIsLoading(true);

            const { country_alfa_2, phone_number, monthly_purchase_volume, pos_count, ...rest } = formValues;

            const payload = {
                ...rest,
                country_alfa_2: country_alfa_2.label,
                phone_number: `+${country_alfa_2.value}${phone_number?.replace(/\s+/g, '')}`,
                monthly_purchase_volume:
                    monthly_purchase_volume?.value === 'D' ? undefined : monthly_purchase_volume?.value,
                pos_count: pos_count?.value,
            };

            await createAccountByFreeTrial(payload)
                .then(async (res) => {
                    if (res.ok) {
                        setAnimationStep(true);
                        const queryParams = new URLSearchParams({
                            utm_campaign: UTM_CAMPAIGN_SUCCESS,
                            utm_source: UTM_SOURCE_SUCCESS,
                            utm_medium: UTM_MEDIUM_SUCCESS,
                            utm_term: UTM_TERM_SUCCESS,
                            utm_content: UTM_CONTENT_SUCCESS,
                            token: encodedToken, // Agregamos el token a la URL
                        });

                        if (checkQuantityDrugManufacturers(formValues?.drug_manufacturer_count, 2)) {
                            router.push(`/signup/success?${queryParams.toString()}`);
                        } else {
                            // push a nueva url en el caso de no haber seleccionado minimo 2 distribuidores
                            router.push(`signup/one-distributor${INIT_FORM_FREE_TRIAL_OPH}`);
                        }
                    } else {
                        const response = await res.json();
                        if (response?.data?.validation) {
                            setFormErrors((prevState) => {
                                const validationErrors = Object.keys(response.data.validation).reduce((acc, key) => {
                                    acc[key] = response.data.validation[key];
                                    return acc;
                                }, {});
                                return {
                                    ...prevState,
                                    ...validationErrors,
                                };
                            });

                            setFormTouched((prevState) => {
                                const validationErrors = Object.keys(response.data.validation).reduce((acc, key) => {
                                    acc[key] = true;
                                    return acc;
                                }, {});
                                return {
                                    ...prevState,
                                    ...validationErrors,
                                };
                            });
                        } else {
                            showErrorModal(MESSAGE.PROCESSING_ERROR);
                        }
                    }
                })
                .catch((err) => {
                    showErrorModal(MESSAGE.PROCESSING_ERROR);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    // const handleChange = (key, value) => {
    //     setFormTouched((prev) => ({
    //         ...prev,
    //         [key]: false, // Resetea el estado de error solo para este campo
    //     }));

    //     setFormErrors((prev) => ({
    //         ...prev,
    //         [key]: false, // Resetea el error solo para este campo
    //     }));

    //     setIsLoading(false);

    //     setFormValues((prevValues) => ({
    //         ...prevValues,
    //         [key]: value,
    //     }));
    // };

    return (
        <div
            className={`${styles['new-container']} ${animationStyles['fade-in-right']} ${
                animationStep ? animationStyles['fade-out-left'] : ''
            }`}
            style={{ height: EX8046 ? 'max-content' : '' }}
            onAnimationEnd={(e) => {
                if (/fade-out-left/.test(e?.animationName)) {
                    setStep(1);
                }
            }}
        >
            <ErrorModal
                open={showModal.state}
                handleClose={setShowModal}
                title={'Error'}
                text={showModal.message}
                buttonText={'Entendido'}
            />
            <div className={styles['wrapper-form']} style={{ padding: 0, height: '100%' }}>
                <div className={styles['form']}>
                    <div
                        style={
                            EX8046
                                ? {
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'flex-start',
                                      gap: isMobile ? 10 : 5,
                                  }
                                : {}
                        }
                    >
                        <h2 className={`${styles['title-form-blue']}`}>Crea tu cuenta</h2>
                        <Disclaimer />
                    </div>
                    <FormRsuite
                        fluid
                        onSubmit={handleSubmit}
                        onChange={(e, event: any) => {
                            setFormErrors((prev) => ({
                                ...prev,
                                [event.target.name]: event.target.value?.trim() === '' ? TEXT.FIELD_REQUIRED : false,
                            }));

                            setFormValues(e);
                        }}
                        formValue={formValues}
                        className={styles['new-form-controls']}
                    >
                        {KEYS.map((key) => {
                            if (key === 'country_alfa_2') {
                                return (
                                    <FormRsuite.Group
                                        key={key}
                                        controlId={key}
                                        className={`${styles['country']} ${styles['mobile-country']}`}
                                        style={{
                                            minWidth: 170,
                                        }}
                                        onFocus={() => handleBlur(key, false)}
                                        onBlur={() => handleBlur(key, true)}
                                        onClick={() => {
                                            if (selectCountryRef.current) {
                                                const inputElement = selectCountryRef.current.root.querySelector(
                                                    'input',
                                                );
                                                if (inputElement) {
                                                    inputElement.focus();
                                                }
                                            }
                                        }}
                                    >
                                        <FormRsuite.ControlLabel>
                                            {isMobile ? LABELS['phone_number'] : LABELS[key]}{' '}
                                            <span className={styles['asterisc']}>*</span>
                                        </FormRsuite.ControlLabel>
                                        <SelectPicker
                                            container={
                                                typeof window !== 'undefined' && parentRef?.current
                                                    ? parentRef.current || document?.body
                                                    : undefined
                                            }
                                            ref={selectCountryRef}
                                            onClose={() => handleBlur(key, true)}
                                            onOpen={() => handleBlur(key, false)}
                                            name={key}
                                            value={formValues?.[key]?.label || null}
                                            onSelect={(value, item, ev) => {
                                                setFormErrors((prev) => ({
                                                    ...prev,
                                                    [key]: !item,
                                                }));
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    [key]: item,
                                                }));
                                            }}
                                            cleanable={false}
                                            data={data}
                                            onClean={() => {
                                                handleBlur(key, false);
                                                setFormErrors((prev) => ({
                                                    ...prev,
                                                    [key]: TEXT.FIELD_REQUIRED,
                                                }));
                                            }}
                                            className={`${styles['select']} ${styles['new-select']} ${
                                                formTouched?.[key] && !!formErrors?.[key] ? styles['country-error'] : ''
                                            }`}
                                            placeholder=" "
                                            searchable={false}
                                            placement="bottom"
                                            renderMenuItem={(label, item) => {
                                                return (
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'flex-start',
                                                            alignItems: 'center',
                                                            gap: 4,
                                                        }}
                                                    >
                                                        <Image
                                                            src={`/assets/images/${label}.svg`}
                                                            alt="arg icon"
                                                            width={16}
                                                            height={16}
                                                        />{' '}
                                                        {label}
                                                    </div>
                                                );
                                            }}
                                            renderValue={(label) => {
                                                return (
                                                    <div>
                                                        <Image
                                                            src={`/assets/images/${label}.svg`}
                                                            alt="arg icon"
                                                            width={16}
                                                            height={16}
                                                        />{' '}
                                                        {label}
                                                    </div>
                                                );
                                            }}
                                        />
                                        <FormRsuite.ErrorMessage
                                            show={formTouched?.[key] && !!formErrors?.[key]}
                                            className={styles['error']}
                                        >
                                            {formErrors?.[key]}
                                        </FormRsuite.ErrorMessage>
                                        <div
                                            ref={parentRef}
                                            style={{ zIndex: 1000, position: 'relative' }}
                                            className={`${styles['dropdown']} ${styles['new-dropdown']}`}
                                        ></div>
                                    </FormRsuite.Group>
                                );
                            }

                            if (key === 'phone_number') {
                                return (
                                    <FormRsuite.Group
                                        key={key}
                                        controlId={key}
                                        className={`${styles['phone']}`}
                                        style={{ maxWidth: 208 }}
                                    >
                                        <FormRsuite.ControlLabel className={styles['phone-label']}>
                                            {LABELS[key]} <span className={styles['asterisc']}>*</span>
                                        </FormRsuite.ControlLabel>
                                        <InputGroup
                                            inside
                                            className={
                                                formTouched?.[key] && !!formErrors?.[key] ? styles['phone-error'] : ''
                                            }
                                            style={{ height: 56 }}
                                            onBlur={() => handleBlur(key, true)}
                                            onFocus={() => handleBlur(key, false)}
                                        >
                                            <InputGroup.Addon className={styles['addon']}>
                                                {!!formValues?.country_alfa_2
                                                    ? `+${formValues?.country_alfa_2.value}`
                                                    : '+52'}
                                            </InputGroup.Addon>
                                            <Input
                                                value={formValues?.[key] || ''}
                                                onChange={handleChangePhone}
                                                placeholder="1 55 1234 5678"
                                                style={{ width: '100%' }}
                                            />
                                        </InputGroup>
                                        <FormRsuite.ErrorMessage
                                            show={formTouched?.[key] && !!formErrors?.[key]}
                                            className={styles['error']}
                                        >
                                            {formErrors?.[key]}
                                        </FormRsuite.ErrorMessage>
                                    </FormRsuite.Group>
                                );
                            }

                            if (key === 'pharmacy_address') {
                                return (
                                    <FormRsuite.Group
                                        key={key}
                                        controlId={key}
                                        className={`${styles['address']}`}
                                        onClick={() => {
                                            const inputElement: any = document.querySelector(
                                                'input[name="pharmacy_address"]',
                                            );
                                            if (inputElement) {
                                                inputElement.focus();
                                            }
                                        }}
                                    >
                                        <FormRsuite.ControlLabel>
                                            {LABELS[key]} <span className={styles['asterisc']}>*</span>
                                        </FormRsuite.ControlLabel>
                                        <ExAutocompleteGmaps
                                            label=""
                                            name={key}
                                            showLabel={false}
                                            isRequired
                                            placeholder=" "
                                            error={formTouched?.[key] && !!formErrors?.[key]}
                                            onSelect={(object) => {
                                                const { location, placeId, value } = object;
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    pharmacy_address: value,
                                                    latitude: location?.lat,
                                                    longitude: location?.lng,
                                                    geohash: placeId,
                                                }));
                                            }}
                                            country={
                                                !!formValues?.country_alfa_2 ? formValues?.country_alfa_2?.label : null
                                            }
                                            value={formValues?.[key]}
                                            onBlur={() => handleBlur(key, true)}
                                            onFocus={() => handleBlur(key, false)}
                                            disabled={false}
                                            maxWidth
                                            addLatitude={true}
                                            autoComplete={'off'}
                                        />
                                        <FormRsuite.ErrorMessage
                                            show={formTouched?.[key] && !!formErrors?.[key]}
                                            className={styles['error']}
                                        >
                                            {formErrors?.[key]}
                                        </FormRsuite.ErrorMessage>
                                    </FormRsuite.Group>
                                );
                            }

                            if (key === 'monthly_purchase_volume') {
                                const selectedCountry = formValues?.country_alfa_2?.label; // Obtiene el país seleccionado
                                const volumeOptions = selectedCountry ? VOLUME_BY_COUNTRY[selectedCountry] || [] : [];

                                return (
                                    <FormRsuite.Group
                                        key={key}
                                        controlId={key}
                                        className={styles['purchase-volumen']}
                                        onFocus={() => handleBlur(key, false)}
                                        onBlur={() => handleBlur(key, true)}
                                    >
                                        <FormRsuite.ControlLabel>{LABELS[key]}</FormRsuite.ControlLabel>
                                        <SelectPicker
                                            container={
                                                typeof window !== 'undefined' && parentRef?.current
                                                    ? parentRef.current || document?.body
                                                    : undefined
                                            }
                                            ref={selectCountryRef}
                                            onClose={() => handleBlur(key, true)}
                                            onOpen={() => handleBlur(key, false)}
                                            name={key}
                                            {...{
                                                valueKey: 'value',
                                                labelKey: 'label',
                                            }}
                                            onSelect={(value, item) => {
                                                setFormErrors((prev) => ({
                                                    ...prev,
                                                    [key]: !item,
                                                }));
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    [key]: item,
                                                }));
                                            }}
                                            cleanable={false}
                                            data={volumeOptions} // Filtra los datos según el país seleccionado
                                            onClean={() => {
                                                handleBlur(key, false);
                                                setFormErrors((prev) => ({
                                                    ...prev,
                                                    [key]: TEXT.FIELD_REQUIRED,
                                                }));
                                            }}
                                            className={`${styles['select']} ${styles['new-select']} ${
                                                formTouched?.[key] && !!formErrors?.[key] ? styles['country-error'] : ''
                                            }`}
                                            placeholder="Selecciona una opción"
                                            disabled={!selectedCountry} // Deshabilita si no hay país seleccionado
                                            searchable={false}
                                            placement="bottom"
                                        />
                                        <FormRsuite.ErrorMessage
                                            show={formTouched?.[key] && !!formErrors?.[key]}
                                            className={styles['error']}
                                        >
                                            {formErrors?.[key]}
                                        </FormRsuite.ErrorMessage>
                                        <div
                                            ref={parentRef}
                                            style={{ zIndex: 1000, position: 'relative' }}
                                            className={`${styles['dropdown']} ${styles['new-dropdown']}`}
                                        ></div>
                                    </FormRsuite.Group>
                                );
                            }

                            if (key === 'pos_count') {
                                return (
                                    <FormRsuite.Group
                                        key={key}
                                        controlId={key}
                                        onFocus={() => handleBlur(key, false)}
                                        onBlur={() => handleBlur(key, true)}
                                    >
                                        <FormRsuite.ControlLabel>
                                            {LABELS[key]} <span className={styles['asterisc']}>*</span>
                                        </FormRsuite.ControlLabel>
                                        <SelectPicker
                                            container={
                                                typeof window !== 'undefined' && parentRef?.current
                                                    ? parentRef.current || document?.body
                                                    : undefined
                                            }
                                            onClose={() => handleBlur(key, true)}
                                            onOpen={() => handleBlur(key, false)}
                                            name={key}
                                            value={formValues?.[key]?.value || ''}
                                            onSelect={(value, item) => {
                                                setFormErrors((prev) => ({
                                                    ...prev,
                                                    [key]: !item,
                                                }));
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    [key]: item,
                                                }));
                                            }}
                                            cleanable={false}
                                            data={[
                                                ...(EX7982 ? [{ label: '1', value: 5 }] : []),
                                                { label: '2 a 10', value: 1 },
                                                { label: '10 a 50', value: 2 },
                                                { label: '50 a 100', value: 3 },
                                                { label: '+100', value: 4 },
                                            ]}
                                            onClean={() => {
                                                handleBlur(key, false);
                                                setFormErrors((prev) => ({
                                                    ...prev,
                                                    [key]: TEXT.FIELD_REQUIRED,
                                                }));
                                            }}
                                            className={`${styles['select']} ${styles['new-select']} ${
                                                formTouched?.[key] && !!formErrors?.[key] ? styles['country-error'] : ''
                                            }`}
                                            placeholder="Selecciona cuántas sucursales tienes"
                                            searchable={false}
                                            placement="bottom"
                                        />
                                        <FormRsuite.ErrorMessage
                                            show={formTouched?.[key] && !!formErrors?.[key]}
                                            className={styles['error']}
                                        >
                                            {formErrors?.[key]}
                                        </FormRsuite.ErrorMessage>
                                        <div
                                            ref={parentRef}
                                            style={{ zIndex: 1000, position: 'relative' }}
                                            className={`${styles['dropdown']} ${styles['new-dropdown']}`}
                                        ></div>
                                    </FormRsuite.Group>
                                );
                            }

                            if (key === 'drug_manufacturer_count') {
                                return (
                                    <FormRsuite.Group
                                        key={key}
                                        controlId={key}
                                        onFocus={() => handleBlur(key, false)}
                                        onBlur={() => handleBlur(key, true)}
                                    >
                                        <FormRsuite.ControlLabel>
                                            Distribuidores <span className={styles['asterisc']}>*</span>
                                        </FormRsuite.ControlLabel>
                                        <DropdownDrugManufacturers
                                            formKey={key}
                                            formErrors={formErrors}
                                            setFormErrors={setFormErrors}
                                            formTouched={formTouched}
                                            country={formValues?.country_alfa_2?.label}
                                            handleBlur={handleBlur}
                                            setFormValues={setFormValues}
                                        />
                                        <FormRsuite.ErrorMessage
                                            show={formTouched?.[key] && !!formErrors?.[key]}
                                            className={styles['error']}
                                        >
                                            {formErrors?.[key]}
                                        </FormRsuite.ErrorMessage>
                                    </FormRsuite.Group>
                                );
                            }

                            if (EX8046 && key === 'purchasing_distributors') {
                                if (
                                    formValues?.drug_manufacturer_count === undefined ||
                                    formValues?.drug_manufacturer_count?.length === 0 ||
                                    checkQuantityDrugManufacturers(formValues?.drug_manufacturer_count, 2)
                                ) {
                                    return;
                                } else {
                                    return (
                                        <>
                                            {includesOthers(formValues?.drug_manufacturer_count) && (
                                                <FormRsuite.Group
                                                    key={key}
                                                    controlId={key}
                                                    className={styles['drug_manufacturer_list']}
                                                >
                                                    <FormRsuite.ControlLabel>
                                                        {isMobile
                                                            ? LABELS['purchasing_distributors_mobile']
                                                            : LABELS[key]}
                                                    </FormRsuite.ControlLabel>
                                                    <FormRsuite.Control
                                                        name={key}
                                                        className={`${styles['input']} ${
                                                            formTouched?.[key] && !!formErrors?.[key]
                                                                ? styles['input-error']
                                                                : ''
                                                        }`}
                                                        onBlur={() => handleBlur(key, true)}
                                                        onFocus={() => handleBlur(key, false)}
                                                        placeholder={
                                                            isMobile
                                                                ? 'Contanos con qué otros'
                                                                : 'Cuéntanos con qué otros distribuidores trabajas'
                                                        }
                                                    />
                                                    <FormRsuite.ErrorMessage
                                                        show={formTouched?.[key] && !!formErrors?.[key]}
                                                        className={styles['error']}
                                                    >
                                                        {formErrors?.[key]}
                                                    </FormRsuite.ErrorMessage>
                                                </FormRsuite.Group>
                                            )}
                                            <div
                                                className={styles['alert']}
                                                style={{
                                                    padding: isMobile ? 8 : '',
                                                    alignItems: isMobile ? 'flex-start' : 'center',
                                                }}
                                            >
                                                <svg
                                                    style={{ minWidth: 24 }}
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M12 17C12.2833 17 12.5208 16.9042 12.7125 16.7125C12.9042 16.5208 13 16.2833 13 16V12C13 11.7167 12.9042 11.4792 12.7125 11.2875C12.5208 11.0958 12.2833 11 12 11C11.7167 11 11.4792 11.0958 11.2875 11.2875C11.0958 11.4792 11 11.7167 11 12V16C11 16.2833 11.0958 16.5208 11.2875 16.7125C11.4792 16.9042 11.7167 17 12 17ZM12 9C12.2833 9 12.5208 8.90417 12.7125 8.7125C12.9042 8.52083 13 8.28333 13 8C13 7.71667 12.9042 7.47917 12.7125 7.2875C12.5208 7.09583 12.2833 7 12 7C11.7167 7 11.4792 7.09583 11.2875 7.2875C11.0958 7.47917 11 7.71667 11 8C11 8.28333 11.0958 8.52083 11.2875 8.7125C11.4792 8.90417 11.7167 9 12 9ZM12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22Z"
                                                        fill="#FFC700"
                                                    />
                                                </svg>
                                                <p>
                                                    Necesitas seleccionar al menos <span>2 distribuidores</span> del
                                                    listado para usar Extendeal.
                                                </p>
                                            </div>
                                        </>
                                    );
                                }
                            }

                            return (
                                <FormRsuite.Group key={key} controlId={key}>
                                    <FormRsuite.ControlLabel>
                                        {LABELS[key]} <span className={styles['asterisc']}>*</span>
                                    </FormRsuite.ControlLabel>
                                    <FormRsuite.Control
                                        name={key}
                                        className={`${styles['input']} ${
                                            formTouched?.[key] && !!formErrors?.[key] ? styles['input-error'] : ''
                                        }`}
                                        onBlur={() => handleBlur(key, true)}
                                        onFocus={() => handleBlur(key, false)}
                                    />
                                    <FormRsuite.ErrorMessage
                                        show={formTouched?.[key] && !!formErrors?.[key]}
                                        className={styles['error']}
                                    >
                                        {formErrors?.[key]}
                                    </FormRsuite.ErrorMessage>
                                </FormRsuite.Group>
                            );
                        })}

                        {/* button */}
                        <span className="button__create-account" style={{ width: '100%' }}>
                            <div className={`button__create-account ${styles['wrapper-button']}`}>
                                <span className="button__create-account">
                                    <ExButton
                                        fullWidth
                                        type="submit"
                                        status="initial"
                                        withArrow={true}
                                        loading={isLoading}
                                        className={`${styles['button']} button__create-account`}
                                    >
                                        {EX8046
                                            ? formValues?.drug_manufacturer_count?.length
                                                ? !checkQuantityDrugManufacturers(
                                                      formValues?.drug_manufacturer_count,
                                                      2,
                                                  )
                                                    ? 'Continuar'
                                                    : 'Crear cuenta'
                                                : 'Crear cuenta'
                                            : 'Crear cuenta'}
                                    </ExButton>
                                </span>
                            </div>
                        </span>
                    </FormRsuite>
                </div>
            </div>
        </div>
    );
};

export default Form;
